import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';

interface QRState {
  qrCodeData: string;
  transactionId: string;
  orderId: string;
  amount: number;
  isWalletRecharge?: boolean;
  cashbackAmount?: number;
  totalCredited?: number;
  redirectUrl?: string;
}

const POLL_INTERVAL = 4000; // 4 seconds
const API_URL = process.env.REACT_APP_API_URL || '';

const PaymentQRPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as QRState | null;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [status, setStatus] = useState<'pending' | 'success' | 'failed'>('pending');
  const pollRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!state?.qrCodeData) {
      navigate('/checkout');
      return;
    }

    // Render QR code onto canvas
    if (canvasRef.current) {
      QRCode.toCanvas(canvasRef.current, state.qrCodeData, { width: 240, margin: 2 });
    }

    // Poll payment status every 4 seconds
    pollRef.current = setInterval(async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/payment/status/${state.transactionId}`, {
          headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        });
        const data = await res.json();
        const txStatus = data?.data?.status;

        if (txStatus === 'SUCCESS') {
          clearInterval(pollRef.current!);
          setStatus('success');
          setTimeout(() => state.isWalletRecharge ? navigate('/wallet') : navigate('/orders'), 2000);
        } else if (txStatus === 'FAILED') {
          clearInterval(pollRef.current!);
          setStatus('failed');
        }
      } catch {
        // silently retry on network error
      }
    }, POLL_INTERVAL);

    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [state, navigate]);

  if (!state) return null;

  return (
    <div className="min-h-screen bg-[#FCFCFC] flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-lg p-10 max-w-sm w-full text-center">

        {status === 'pending' && (
          <>
            <h2 className="text-xl font-bold text-gray-800 mb-1">Scan & Pay</h2>
            <p className="text-sm text-gray-500 mb-6">
              {state.isWalletRecharge
                ? `Pay ₹${state.amount.toFixed(0)} to add to wallet${state.cashbackAmount ? ` + ₹${state.cashbackAmount} cashback` : ''}`
                : `Use any UPI app to scan and pay ₹${state.amount.toFixed(0)}`}
            </p>
            <div className="flex justify-center mb-6">
              <canvas ref={canvasRef} className="rounded-lg border border-gray-100" />
            </div>
            <p className="text-xs text-gray-400 mb-2">UPI ID or scan with PhonePe / GPay / Paytm</p>
            <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-[#DAA520]" />
              Waiting for payment confirmation...
            </div>
            <button
              onClick={() => navigate('/orders')}
              className="mt-6 text-sm text-gray-400 underline hover:text-gray-600"
            >
              Cancel and go to orders
            </button>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="text-5xl mb-4">✅</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Payment Successful!</h2>
            {state.isWalletRecharge && state.totalCredited && (
              <p className="text-green-600 font-semibold mb-1">₹{state.totalCredited.toFixed(2)} credited to wallet 🎉</p>
            )}
            <p className="text-gray-500">Redirecting...</p>
          </>
        )}

        {status === 'failed' && (
          <>
            <div className="text-5xl mb-4">❌</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Payment Failed</h2>
            <p className="text-gray-500 mb-6">Please try again or choose Cash on Delivery.</p>
            <button
              onClick={() => navigate('/checkout')}
              className="bg-[#E91E63] text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#D81B60] w-full"
            >
              Try Again
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentQRPage;
